// Resource module — model registry (/models) + version listing. React-free.
// Post-envelope wire shapes: GET /models → { models: Model[]; total }
// (total = full org-scoped count honoring the same filters, NOT capped by
// limit); GET/PATCH /models/:id → { model }; versions → { versions } / { version }.
import { apiFetch } from '@/lib/api'
import type { Model, ModelVersion } from '@/types/api'

/** Peeled shape of GET /models — the list page as served: rows + org total. */
export type ModelListPage = { models: Model[]; total: number }

// Key contract: [domain, orgId, leaf, ...params] — see OrgQueryKey in @/lib/queries.
// `all` is the domain prefix: invalidating it refreshes every models query for the org.
export const modelsKeys = {
  all: (orgId: string | null) => ['models', orgId] as const,
  list: (orgId: string | null) => ['models', orgId, 'list'] as const,
  palette: (orgId: string | null) => ['models', orgId, 'palette'] as const,
  detail: (modelId: string) => (orgId: string | null) => ['models', orgId, 'detail', modelId] as const,
  /** Version list — shared by pickers and the detail page (was split into
   *  'model-versions'/'model-versions-list' roots; one entry now). */
  versions: (modelId: string) => (orgId: string | null) => ['models', orgId, 'versions', modelId] as const,
  version: (modelId: string, versionId: string) => (orgId: string | null) => ['models', orgId, 'version', modelId, versionId] as const,
}

export function fetchModels(orgId: string, params?: { limit?: number | string }): Promise<ModelListPage> {
  return apiFetch<ModelListPage>('/models', { orgId, params: params?.limit !== undefined ? { limit: params.limit } : undefined })
}

export async function fetchModel(orgId: string, modelId: string): Promise<Model> {
  return (await apiFetch<{ model: Model }>(`/models/${modelId}`, { orgId })).model
}

export async function fetchModelVersions(orgId: string, modelId: string): Promise<ModelVersion[]> {
  return (await apiFetch<{ versions: ModelVersion[] }>(`/models/${modelId}/versions`, { orgId })).versions
}

export async function fetchModelVersion(orgId: string, modelId: string, versionId: string): Promise<ModelVersion> {
  return (await apiFetch<{ version: ModelVersion }>(`/models/${modelId}/versions/${versionId}`, { orgId })).version
}

// BYO-adapter version create (ADR-0045): the only tenant blobs path left on
// POST /models/:m/versions. The adapter version lands `available` under the
// BASE model row with the parent's detectionResult copied by the backend
// (adapter manifests hold only safetensors — detection cannot run on them).
// Weight-file/manifest creation is operator-only now (403
// model-registry.OPERATOR_ONLY) and has no console caller.
export type AdapterVersionInput = {
  blobs: Array<{ sha256: string; size: number; originalPath: string }>
  /** Parent = an `available` version of THIS model carrying a detectionResult. */
  parentVersionId: string
  tag?: string
}

export async function createAdapterVersion(
  orgId: string,
  modelId: string,
  input: AdapterVersionInput,
): Promise<ModelVersion> {
  return (await apiFetch<{ version: ModelVersion }>(`/models/${modelId}/versions`, {
    method: 'POST',
    orgId,
    body: JSON.stringify({
      blobs: input.blobs,
      lineageType: 'fine_tune',
      parentVersionId: input.parentVersionId,
      ...(input.tag ? { tag: input.tag } : {}),
    }),
  })).version
}
